import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Search for pathology filings based on multiple criteria
 */
export const searchFilings = createAsyncThunk(
  "pathologySearch/searchFilings",
  async ({ searchParams, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/search_filings`,
        {
          ...searchParams,
          license_key,
        },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/**
 * Fetch detailed filing information by lab_report_id
 */
export const fetchFilingDetail = createAsyncThunk(
  "pathologySearch/fetchFilingDetail",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/fetch_blood_filing`,
        {
          lab_report_id,
          liverun,
          license_key,
        },
      );
      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// === SLICE ===

const pathologyFrameSlice = createSlice({
  name: "pathologySearch",
  initialState: {
    // Search parameters
    searchParams: {
      nhs_number: "",
      pathology_category: "",
      subtest_name: "",
      start_filing_date: "",
      end_filing_date: "",
      liverun: true,
    },

    // Search results
    searchResults: [],
    totalResults: 0,

    // Selected filing detail
    selectedFiling: null,
    selectedLabReportId: null,

    // Loading states
    loadingSearch: false,
    loadingDetail: false,

    // Error states
    searchError: null,
    detailError: null,
  },

  reducers: {
    /**
     * Update a single search parameter
     */
    updateSearchParam(state, action) {
      const { field, value } = action.payload;
      state.searchParams[field] = value;
    },

    /**
     * Update multiple search parameters at once
     */
    updateSearchParams(state, action) {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },

    /**
     * Clear all search parameters
     */
    clearSearchParams(state) {
      state.searchParams = {
        nhs_number: "",
        pathology_category: "",
        subtest_name: "",
        subtest_min: "",
        subtest_max: "",
        keyword: "",
        medication: "",
        problem_keyword: "",
        age_min: "",
        age_max: "",
        gender: "",
        message_keyword: "",
        liverun: state.searchParams.liverun,
      };
      state.searchResults = [];
      state.totalResults = 0;
      state.searchError = null;
    },

    /**
     * Clear search results
     */
    clearSearchResults(state) {
      state.searchResults = [];
      state.totalResults = 0;
    },

    /**
     * Set selected lab report ID
     */
    setSelectedLabReportId(state, action) {
      state.selectedLabReportId = action.payload;
    },

    /**
     * Clear selected filing detail
     */
    clearFilingDetail(state) {
      state.selectedFiling = null;
      state.selectedLabReportId = null;
      state.detailError = null;
    },

    /**
     * Toggle liverun mode
     */
    toggleLiverun(state) {
      state.searchParams.liverun = !state.searchParams.liverun;
    },
  },

  extraReducers: (builder) => {
    builder
      // Search Filings
      .addCase(searchFilings.pending, (state) => {
        state.loadingSearch = true;
        state.searchError = null;
      })
      .addCase(searchFilings.fulfilled, (state, action) => {
        state.loadingSearch = false;

        // Handle different response formats
        if (Array.isArray(action.payload)) {
          state.searchResults = action.payload.map((filing, idx) => ({
            id: filing._id?.$oid || filing.id || idx,
            lab_report_id:
              filing.patient_data?.lab_report_id || filing.lab_report_id,
            emis_id: filing.patient_data?.emis_id || filing.emis_id,
            category: Array.isArray(filing.category_names)
              ? filing.category_names.join(", ")
              : filing.category_names || "",
            category_type: filing.patient_data?.is_normal ?? true,
            status: filing.result || "Unknown",
            starttime: filing.start_time?.$date
              ? new Date(filing.start_time.$date).toLocaleString()
              : filing.start_time || "",
            notes: Array.isArray(filing.actions)
              ? filing.actions.map((a) => a.msg || "").join(" | ")
              : "",
          }));
          state.totalResults = action.payload.length;
        } else if (action.payload.results) {
          // If API returns paginated results
          state.searchResults = action.payload.results.map((filing, idx) => ({
            id: filing._id?.$oid || filing.id || idx,
            lab_report_id:
              filing.patient_data?.lab_report_id || filing.lab_report_id,
            emis_id: filing.patient_data?.emis_id || filing.emis_id,
            category: Array.isArray(filing.category_names)
              ? filing.category_names.join(", ")
              : filing.category_names || "",
            category_type: filing.patient_data?.is_normal ?? true,
            status: filing.result || "Unknown",
            starttime: filing.start_time?.$date
              ? new Date(filing.start_time.$date).toLocaleString()
              : filing.start_time || "",
            notes: Array.isArray(filing.actions)
              ? filing.actions.map((a) => a.msg || "").join(" | ")
              : "",
          }));
          state.totalResults =
            action.payload.total || action.payload.results.length;
        }
      })
      .addCase(searchFilings.rejected, (state, action) => {
        state.loadingSearch = false;
        state.searchError = action.error.message || "Failed to search filings";
      })

      // Fetch Filing Detail
      .addCase(fetchFilingDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchFilingDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.selectedFiling = action.payload;
      })
      .addCase(fetchFilingDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError =
          action.error.message || "Failed to fetch filing detail";
      });
  },
});

export const {
  updateSearchParam,
  updateSearchParams,
  clearSearchParams,
  clearSearchResults,
  setSelectedLabReportId,
  clearFilingDetail,
  toggleLiverun,
} = pathologyFrameSlice.actions;

export default pathologyFrameSlice.reducer;
